import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { StanFilterValue } from '../types';
import { NUM_OP_LABELS } from '../utils/filterOperators';
import { isNumOp, NumOp } from '../utils/filterTypes';

type UseNumberFilterArgs = {
    filterItem: any;
    onApply: (filters: StanFilterValue[]) => void;
    onClearFilter?: (id: string) => void;
    disabled?: boolean;
    closeMenu: () => void;
};

export const useNumberFilter = ({ filterItem, onApply, onClearFilter, closeMenu }: UseNumberFilterArgs) => {
    const [value, setValue] = useState<string>('');
    const [operator, setOperator] = useState<NumOp>('eq');
    const [appliedValue, setAppliedValue] = useState<string>('');

    const summary = useMemo(() => (appliedValue !== '' ? `${appliedValue}` : ''), [appliedValue]);
    const operatorOptions = Object.entries(NUM_OP_LABELS).map(([value, label]) => ({ label, value }));

    const resetState = useCallback(() => {
        setValue('');
        setOperator('eq');
        setAppliedValue('');
    }, []);

    const clearFilters = useCallback(
        (event?: MouseEvent) => {
            event?.stopPropagation();
            resetState();
            onApply([]);
            onClearFilter?.(filterItem.id);
            closeMenu();
        },
        [closeMenu, filterItem.id, onApply, onClearFilter, resetState]
    );

    // Initialize state from filterItem.initFilterValue
    useEffect(() => {
        const init = filterItem.initFilterValue;
        if (init === null) {
            resetState();
            closeMenu();
        } else if (init) {
            setValue(init.value || '');
            const op = init.operatorCode;
            setOperator(isNumOp(op) ? op : 'eq');
            setAppliedValue(init.value || '');
        }
    }, [filterItem.initFilterValue, resetState, closeMenu]);

    const apply = useCallback(() => {
        if (value === '') {
            onApply([]);
            setAppliedValue('');
            closeMenu();
            return;
        }
        const out: StanFilterValue[] = [
            {
                id: filterItem.id,
                operator,
                value: String(value),
                type: 'number',
            },
        ];
        onApply(out);
        setAppliedValue(value);
        closeMenu();
    }, [closeMenu, filterItem.id, onApply, operator, value]);

    const handleOperatorSelect = useCallback((value: string) => {
        if (isNumOp(value)) {
            setOperator(value);
        }
    }, []);

    const currentOperator = operatorOptions.find(opt => opt.value === operator) ?? null;

    /**
     * Handles input change with validation
     * Extracted for better separation of concerns
     */
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    };

    /**
     * Handles operator selection with type safety
     * Extracted for better separation of concerns
     */
    const handleOperatorChange = (value: string) => {
        handleOperatorSelect(value);
    };

    const hasSummary = summary.length > 0;

    return {
        summary,
        hasSummary,
        value,
        setValue,
        operatorOptions,
        currentOperator,
        handleOperatorSelect,
        apply,
        clearFilters,
        // New functionality extracted from component
        handleInputChange,
        handleOperatorChange,
    };
};
